import { useCallback, useSyncExternalStore } from 'react';
import { api } from './api';

/**
 * Per-user interface text size, layered on top of the platform text size (Platform → Appearance).
 * Stored on the user (`PATCH /me/preferences { fontSizePreference }`) so it follows them across
 * devices, and mirrored in localStorage so the size applies before the session loads after a refresh.
 */
export type FontSizePreference = 'small' | 'default' | 'large';

export const FONT_SIZE_OPTIONS: Array<{ value: FontSizePreference; label: string; description: string }> = [
  { value: 'small', label: 'Small', description: 'Fits more on screen' },
  { value: 'default', label: 'Default', description: 'Recommended' },
  { value: 'large', label: 'Large', description: 'Easier to read' },
];

/** Multiplies the platform root size: 15 / 16 / 18 px at the platform default. */
export const USER_FONT_FACTOR: Record<FontSizePreference, number> = { small: 0.9375, default: 1, large: 1.125 };

const STORAGE_KEY = 'cop.fontSize';
const listeners = new Set<() => void>();

const isPref = (v: unknown): v is FontSizePreference => v === 'small' || v === 'default' || v === 'large';

function readStored(): FontSizePreference {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isPref(v) ? v : 'default';
  } catch {
    return 'default';
  }
}

let current: FontSizePreference = typeof window === 'undefined' ? 'default' : readStored();

export function getFontSizePreference(): FontSizePreference {
  return current;
}

/** Sets the local value (and storage) without calling the API; notifies subscribers. */
export function setLocalFontSize(pref: FontSizePreference) {
  if (!isPref(pref) || pref === current) return;
  current = pref;
  try {
    window.localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    /* storage unavailable: the in-memory value still applies */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useFontSizePreference(signedIn: boolean) {
  const value = useSyncExternalStore(subscribe, getFontSizePreference, () => 'default' as FontSizePreference);
  const set = useCallback(
    async (pref: FontSizePreference) => {
      const previous = getFontSizePreference();
      setLocalFontSize(pref);
      if (!signedIn) return;
      try {
        await api.patch('/me/preferences', { fontSizePreference: pref });
      } catch (e) {
        setLocalFontSize(previous);
        throw e;
      }
    },
    [signedIn],
  );
  return [value, set] as const;
}

/** Adopts the server value once the session loads (another device may have changed it). */
export function adoptServerFontSize(pref: string | undefined | null) {
  if (isPref(pref)) setLocalFontSize(pref);
}
